import type { JourDuCalendrier } from '../partage/api/types';
import { decaler, jourLocal } from '../partage/datesEnMots';

/**
 * Le calcul de l'Agenda au téléphone (ticket 10 de telephone) : une semaine, du lundi au
 * dimanche, prise dans la grille d'un mois que le serveur envoie déjà (`GET /api/calendrier`).
 *
 * La grille d'un mois va du lundi de la semaine du 1er au dimanche de la semaine du dernier
 * jour : une semaine dont le lundi est dans le mois y est donc toujours entière, même quand
 * elle finit dans le mois suivant. Rien n'est inventé ici : les jours, ce qui s'y passe,
 * « passé » et « aujourd'hui » viennent du serveur. On ne calcule que des jours de calendrier.
 */

/** Le lundi de la semaine d'un jour (la semaine commence le lundi, comme le Calendrier). */
export function lundiDe(jour: string): string {
  const jourDeLaSemaine = jourLocal(jour)?.getDay() ?? 1;
  return decaler(jour, -((jourDeLaSemaine + 6) % 7));
}

/** Le mois « aaaa-mm » dont la grille porte la semaine de ce lundi. */
export function moisDeLaSemaine(lundi: string): string {
  return lundi.slice(0, 7);
}

/** Aujourd'hui selon le serveur, s'il est dans la grille. */
export function aujourdhuiDans(jours: JourDuCalendrier[]): string | null {
  return jours.find((j) => j.estAujourdhui)?.jour ?? null;
}

/** Les sept jours de la semaine de ce lundi, pris dans la grille ; `null` s'il en manque un. */
export function semaineDans(jours: JourDuCalendrier[], lundi: string): JourDuCalendrier[] | null {
  const parJour = new Map(jours.map((j) => [j.jour, j]));
  const semaine: JourDuCalendrier[] = [];
  for (let i = 0; i < 7; i += 1) {
    const j = parJour.get(decaler(lundi, i));
    if (!j) return null;
    semaine.push(j);
  }
  return semaine;
}

/**
 * Les bulles à exporter pour une semaine : ses rendez-vous et ses échéances, chacune une
 * fois, dans l'ordre des jours. Les tâches d'une journée prévue n'en sont pas : elles sont à
 * toi, pas au monde.
 */
export function bullesAExporter(semaine: JourDuCalendrier[]): string[] {
  const vues = new Set<string>();
  for (const j of semaine) {
    for (const e of j.echeances) vues.add(e.bulleId);
    for (const r of j.rendezVous) vues.add(r.bulleId);
  }
  return [...vues];
}

/** Ce qu'on peut faire d'un jour ouvert : le préparer, planifier aujourd'hui, ou rien (passé). */
export type GesteDuJour = 'preparer' | 'changer' | 'planifier' | 'changer-aujourdhui' | null;

export function gesteDuJour(j: Pick<JourDuCalendrier, 'passe' | 'estAujourdhui' | 'aPreparer' | 'journee'>): GesteDuJour {
  if (j.estAujourdhui) return j.journee ? 'changer-aujourdhui' : 'planifier';
  if (j.aPreparer && !j.passe) return j.journee ? 'changer' : 'preparer';
  return null;
}
